import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CSV_FORMULA_REJECTION_MESSAGE, hasSpreadsheetFormulaPrefix } from '@access-cms/shared-constants';

import { Button, ButtonVariation } from '../../../../components/Button';
import Dropdown from '../../../../components/Dropdown';
import type { DropdownOption } from '../../../../components/Dropdown';
import { FileUpload, UploadedFile } from '../../../../components/FileUpload';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import { RequiredFieldsLegend } from '../../../../components/RequiredFieldsLegend';
import TextField from '../../../../components/TextField';
import { ValidationAnnouncement } from '../../../../components/ValidationAnnouncement';
import { useValidationAnnouncement } from '../../../../hooks/useValidationAnnouncement';
import './LaborCategoryModal.css';

export enum LaborCategoryModalMode {
  ADD = 'add',
  EDIT = 'edit',
}

export interface LaborCategoryData {
  name: string;
  riskLevel: string;
  files: UploadedFile[];
}

export interface LaborCategoryModalProps {
  contractId?: string;
  contractNumber: string;
  laborCategoryId?: string;
  riskLevelOptions: DropdownOption<string>[];
  mode: LaborCategoryModalMode;
  initialData?: LaborCategoryData;
  isSubmitting?: boolean;
  onSave: (data: LaborCategoryData) => void;
  onCancel: () => void;
  onGetHelp?: () => void;
  onFileClick?: (file: UploadedFile) => void;
}

export function LaborCategoryModal({
  contractNumber,
  riskLevelOptions,
  mode,
  initialData,
  isSubmitting = false,
  onSave,
  onCancel,
  onGetHelp,
  onFileClick,
}: LaborCategoryModalProps) {
  const baseTestId = 'labor-category-modal';
  const [name, setName] = useState(initialData?.name || '');
  const [riskLevel, setRiskLevel] = useState(initialData?.riskLevel || '');
  const [files, setFiles] = useState<UploadedFile[]>(initialData?.files || []);
  // Set by a Save attempt on an incomplete form, so required fields are not reported
  // before the user has tried to submit.
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  // Mirror `files` state through a ref so handleSave always reads the latest value,
  // even if a stale closure would otherwise see an empty array. useLayoutEffect runs
  // synchronously after DOM mutations (before paint), guaranteeing the ref is current
  // before any click event handler fires.
  const filesRef = useRef<UploadedFile[]>(files);
  useLayoutEffect(() => {
    filesRef.current = files;
  });
  // Guards the initialData-sync effect below: once the user makes a local edit,
  // subsequent initialData changes (e.g. query refetch) must not overwrite their input.
  const hasLocalEditsRef = useRef(false);

  // Seed form state from parent-provided initialData. Skips sync if the user has
  // made local edits or a save is in flight, preventing overwrites mid-edit.
  useEffect(() => {
    if (hasLocalEditsRef.current || isSubmitting) {
      return;
    }

    setName(initialData?.name || '');
    setRiskLevel(initialData?.riskLevel || '');
    setFiles(initialData?.files || []);
  }, [initialData, isSubmitting]);

  const handleFilesChange = (updatedFiles: UploadedFile[]) => {
    hasLocalEditsRef.current = true;
    setFiles(updatedFiles);
  };

  const handleSave = () => {
    // ACP-2092 (Section 508): Save used to be disabled until the form was complete, so a
    // screen reader user pressed it and heard nothing, with no way to learn what was
    // missing. It stays actionable and reports instead — the same change made to the
    // Add User to Roster modal.
    if (!isFormValid) {
      setHasAttemptedSave(true);
      reportInvalidFields();

      return;
    }

    onSave({
      files: filesRef.current,
      name,
      riskLevel,
    });
  };

  // A category name reaches the roster CSV export, so a formula-shaped value is
  // refused here as well as server-side [ACP-2142]. Only surfaced once something
  // has been typed, so the field does not open in an error state.
  const nameError =
    name.trim() !== '' && hasSpreadsheetFormulaPrefix(name.trim()) ? CSV_FORMULA_REJECTION_MESSAGE : undefined;

  // Validates both at form level (button enable) and at save time (validatePdtFileRequired).
  // The file-name check is defensive: in some environments (Windows + ZScaler VPN),
  // the browser File API can produce a File object with a valid size but empty name,
  // which would pass the size check yet fail the save validation.
  const isFormValid =
    name.trim() !== '' &&
    !nameError &&
    riskLevel !== '' &&
    files.length > 0 &&
    !!files[0]?.name?.trim() &&
    (mode === LaborCategoryModalMode.ADD ? (files[0]?.file?.size ?? 0) > 0 : true);
  const isFileValid =
    files.length > 0 &&
    !!files[0]?.name?.trim() &&
    (mode === LaborCategoryModalMode.ADD ? (files[0]?.file?.size ?? 0) > 0 : true);
  const nameFieldError =
    nameError ?? (hasAttemptedSave && name.trim() === '' ? 'Labor Category is required' : undefined);
  const riskLevelError = hasAttemptedSave && riskLevel === '' ? 'Risk Level is required' : undefined;
  const fileError = hasAttemptedSave && !isFileValid ? 'Position Designation Tool (PDT) is required' : undefined;
  const {
    announcement: errorAnnouncement,
    reportInvalidFields,
    scopeRef,
  } = useValidationAnnouncement<HTMLDivElement>({
    errorSignature: [nameFieldError && 'name', riskLevelError && 'risk', fileError && 'file'].filter(Boolean).join('|'),
  });
  const saveButtonLabel = mode === LaborCategoryModalMode.ADD ? 'Add a Labor Category' : 'Save Changes';
  const saveLoadingText = mode === LaborCategoryModalMode.ADD ? 'Adding...' : 'Updating...';

  return (
    <div
      ref={scopeRef}
      className='labor-category-modal'
      data-testid={baseTestId}>
      <ValidationAnnouncement
        data-testid={`${baseTestId}-error-announcement`}
        message={errorAnnouncement}
      />
      <p
        className='labor-category-modal__hint'
        data-testid={`${baseTestId}-hint`}>
        Fill out the required fields to add a Labor Category to Contract {contractNumber}.
      </p>
      <RequiredFieldsLegend data-testid={`${baseTestId}-required-fields`} />

      <div
        className='labor-category-modal__form'
        data-testid={`${baseTestId}-form`}>
        <TextField
          required
          data-testid={`${baseTestId}-name`}
          disabled={isSubmitting}
          errorMessage={nameFieldError}
          label='Labor Category'
          name='laborCategory'
          tooltipText='Enter the title of the Labor Category.'
          value={name}
          onChange={(e) => {
            hasLocalEditsRef.current = true;
            setName(e.target.value);
          }}
        />

        <Dropdown
          required
          data-testid={`${baseTestId}-risk-level`}
          defaultValue={riskLevel}
          disabled={isSubmitting}
          errorMessage={riskLevelError}
          label='Risk Level'
          name='riskLevel'
          options={riskLevelOptions}
          tooltipText='Select the Risk Level for this Labor Category.'
          value={riskLevel ?? undefined}
          onChange={(value) => {
            hasLocalEditsRef.current = true;
            setRiskLevel(value);
          }}
        />

        <FileUpload
          allowLocalFilePreview
          acceptedFormats={['PDF']}
          data-testid={`${baseTestId}-upload`}
          errorMessage={fileError}
          files={files}
          label='Upload Position Designation Tool (PDT)'
          maxFiles={1}
          maxSizeMB={4}
          required={true}
          tooltipText='Upload the updated Position Designation Tool (PDT) pdf document for this Labor Category.'
          onFileClick={onFileClick}
          onFilesChange={handleFilesChange}
        />
      </div>

      <div
        className='labor-category-modal__actions'
        data-testid={`${baseTestId}-actions`}>
        <div
          className='labor-category-modal__actions-left'
          data-testid={`${baseTestId}-actions-left`}>
          {onGetHelp && (
            <HelpDrawerLink
              data-testid={`${baseTestId}-help`}
              text='Get Help'
              onClick={onGetHelp}
            />
          )}
        </div>
        <div
          className='labor-category-modal__actions-right'
          data-testid={`${baseTestId}-actions-right`}>
          <Button
            isAutoWidth
            data-testid={`${baseTestId}-cancel`}
            disabled={isSubmitting}
            variation={ButtonVariation.GHOST}
            onClick={onCancel}>
            Cancel
          </Button>
          <Button
            isAutoWidth
            data-testid={`${baseTestId}-save`}
            disabled={isSubmitting}
            isLoading={isSubmitting}
            loadingText={saveLoadingText}
            variation={ButtonVariation.SOLID}
            onClick={handleSave}>
            {saveButtonLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default LaborCategoryModal;
